/**
 * iter73b — Countdown timer for the Ready-Made sale.
 *
 * Updates once per second. Auto-hides once the end time has passed
 * (parent should also stop rendering it, but we self-guard so a stale
 * mount doesn't render "0d 0h 0m 0s" indefinitely).
 *
 * Props:
 *   endsAt   — ISO string (UTC).
 *   className — extra classes for the wrapper.
 *   compact  — hide seconds on tight layouts (banner strip).
 *   testid   — data-testid root.
 */
import React, { useEffect, useState } from "react";

function _diff(endMs) {
  const now = Date.now();
  const ms = Math.max(0, endMs - now);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const seconds = Math.floor((ms / 1000) % 60);
  return { ms, days, hours, minutes, seconds, done: ms <= 0 };
}

export default function SaleCountdown({
  endsAt,
  className = "",
  compact = false,
  testid = "sale-countdown",
}) {
  const endMs = endsAt ? new Date(endsAt).getTime() : 0;
  const [t, setT] = useState(() => _diff(endMs));

  useEffect(() => {
    if (!endMs) return;
    setT(_diff(endMs));
    const id = setInterval(() => setT(_diff(endMs)), 1000);
    return () => clearInterval(id);
  }, [endMs]);

  if (!endsAt || t.done) return null;

  const chip = "font-mono tabular-nums";
  return (
    <span className={`inline-flex items-baseline gap-1 ${className}`} data-testid={testid}>
      <span className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase mr-1">
        Ends in
      </span>
      {t.days > 0 && (
        <span className={chip} data-testid={`${testid}-d`}>
          {t.days}<span className="text-zinc-500">d</span>
        </span>
      )}
      <span className={chip} data-testid={`${testid}-h`}>
        {String(t.hours).padStart(2, "0")}<span className="text-zinc-500">h</span>
      </span>
      <span className={chip} data-testid={`${testid}-m`}>
        {String(t.minutes).padStart(2, "0")}<span className="text-zinc-500">m</span>
      </span>
      {!compact && (
        <span className={chip} data-testid={`${testid}-s`}>
          {String(t.seconds).padStart(2, "0")}<span className="text-zinc-500">s</span>
        </span>
      )}
    </span>
  );
}
